import {
    classifyLayer1,
    classifyLayer2,
    type DetectGpuRawResult,
    isWindows,
    type WebGlProbeResult,
    type WebGpuProbeResult,
} from "utils/gpuDetection";

import { buildGuidanceItems, classifySituation, isGhostNudgeEligible } from "./classifySituation";
import { type LifecycleWriter, sharedDialogLifecycle } from "./dialogLifecycle";
import { getCachedDetectGpuRaw, getCachedWebGl2, getCachedWebGpu } from "./gpuProbeCache";
import { type GuidanceSnapshot } from "./guidanceModel";

export type RunGpuDetectionInput = {
    enabled?: boolean;
    tier?: number;
    lifecycleWriter?: LifecycleWriter;
    detectGpuRaw?: DetectGpuRawResult;
};

const SKIPPED_WEBGPU: WebGpuProbeResult = { status: "unsupported" };
const SKIPPED_DETECT_GPU: DetectGpuRawResult = { status: "failed" };

/**
 * Headless detection entry point (no `View` required).
 *
 * This exists so load-time paths can classify situation A before engine init succeeds
 * (consumed by BM-13409). The session hook (`useGpuDetection`) remains the runtime path
 * for view-dependent watchdog signals.
 * Callers must pass `enabled: true` after evaluating `selectGpuGuidanceEnabled`.
 * When omitted or false, this function returns `null` and performs no probes.
 *
 * Situation A is decided by the synchronous WebGL2 probe. WebGPU and detect-gpu are
 * skipped in that case so a hung adapter request cannot block the hard-stop snapshot.
 * When WebGL is hardware they still run — that is the scenario-3 guard (healthy WebGL +
 * missing WebGPU must stay `degraded`, not acceleration-off).
 */
export async function runGpuDetection(input: RunGpuDetectionInput = {}): Promise<GuidanceSnapshot | null> {
    if (!input.enabled) {
        return null;
    }

    const lifecycle = input.lifecycleWriter ?? sharedDialogLifecycle;
    const webgl = getCachedWebGl2();

    if (webgl.status === "unavailable" || webgl.status === "software") {
        return buildSnapshot({
            webgl,
            webgpu: SKIPPED_WEBGPU,
            detectGpuRaw: input.detectGpuRaw ?? SKIPPED_DETECT_GPU,
            tier: input.tier,
            lifecycle,
        });
    }

    const webgpu = await getCachedWebGpu();
    const detectGpuRaw = input.detectGpuRaw ?? (await getCachedDetectGpuRaw());
    return buildSnapshot({
        webgl,
        webgpu,
        detectGpuRaw,
        tier: input.tier,
        lifecycle,
    });
}

function buildSnapshot({
    webgl,
    webgpu,
    detectGpuRaw,
    tier,
    lifecycle,
}: {
    webgl: WebGlProbeResult;
    webgpu: WebGpuProbeResult;
    detectGpuRaw: DetectGpuRawResult;
    tier?: number;
    lifecycle: LifecycleWriter;
}): GuidanceSnapshot {
    const layer1 = classifyLayer1(webgl, webgpu);
    const renderer = webgl.renderer ?? webgpu.adapterInfo?.description ?? webgpu.adapterInfo?.device;
    const layer2 = classifyLayer2({
        renderer,
        tier,
        detectGpu: detectGpuRaw,
        webGpuAdapterInfo: webgpu.adapterInfo,
    });

    const windows = isWindows({ renderer, webGpuAdapterInfo: webgpu.adapterInfo });
    const situation = classifySituation({
        layer1,
        layer2,
        isWindows: windows,
    });
    const lifecycleDecision = lifecycle.evaluate(situation);
    const ghostNudgePending = lifecycle.isGhostNudgePending(
        isGhostNudgeEligible({ isWindows: windows, layer2, situation }),
    );

    const items = buildGuidanceItems({
        isWindows: windows,
        layer1,
        layer2,
    });

    return {
        situation,
        lifecycle: lifecycleDecision,
        items,
        isWindows: windows,
        ghostNudgePending,
        renderer,
        layer1,
        layer2,
        webgl,
        webgpu,
        detectGpuRaw,
    };
}
